import { FC } from 'react'

import {
  faFlag,
  faComment,
  faCirclePlay,
  faArrowRight,
} from '@fortawesome/pro-solid-svg-icons'
import { ONBOARDING_CARD } from './copyConstants'
import { Card, CardColors, Text, Link, List, ListCustomBullets } from '@/ui'
import { TypographyStyles } from '@/styles'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'

const TransactionsOnboardingCard: FC = () => {
  const currentUser = useReselector(getCurrentUser)
  const userSpecificKey = currentUser?.id
    ? `${ONBOARDING_CARD.LOCAL_STORAGE_KEY}-user-${currentUser.id}`
    : ONBOARDING_CARD.LOCAL_STORAGE_KEY

  return (
    <Card
      color={CardColors.LEVEL_1}
      dismissible
      permanentDismissalKey={userSpecificKey}
    >
      <div className="@container">
        <div className="flex flex-col @min-[800px]:flex-row gap-5">
          <div
            className="hidden @min-[800px]:block rounded-xl overflow-hidden shadow-sm mr-4 self-start flex-shrink-0"
            style={{ width: 380, maxHeight: 320, aspectRatio: '16 / 11.58' }}
          >
            <iframe
              src="https://www.loom.com/embed/3ba4041bcef74cc4b361b9fa82f28c15"
              title="Transactions walkthrough video"
              frameBorder="0"
              allowFullScreen
              sandbox="allow-scripts allow-same-origin allow-popups"
              style={{
                width: '100%',
                height: '100%',
              }}
            />
          </div>
          <div className="flex flex-col gap-5 self-start">
            <div className="flex flex-col gap-2">
              <Text variant={TypographyStyles.EYEBROW}>
                {ONBOARDING_CARD.SECTION_LABEL}
              </Text>
              <Text variant={TypographyStyles.DISPLAY_XS}>
                {ONBOARDING_CARD.TITLE}
              </Text>
            </div>

            <Text
              variant={TypographyStyles.BODY_SM}
              className="text-neutral-600"
            >
              {ONBOARDING_CARD.BODY}
            </Text>

            <ListCustomBullets variant="icon">
              <ListCustomBullets.Item
                icon={faFlag}
                heading="Mark personal transactions"
              >
                <Text variant={TypographyStyles.LABEL_MD}>
                  Mark personal transactions
                </Text>
                <Text
                  variant={TypographyStyles.BODY_SM}
                  className="text-neutral-600"
                >
                  Go to the Type column and select <strong>Personal</strong>{' '}
                  from the dropdown to remove it from your business books.{' '}
                  <Link
                    href="https://support.joinheard.com/hc/en-us/articles/23407015266967-Using-a-dedicated-business-bank-account-for-your-practice#risks_of_commingled_account"
                    newPage
                  >
                    Why?
                  </Link>
                </Text>
              </ListCustomBullets.Item>
              <ListCustomBullets.Item
                icon={faComment}
                heading="Add context to unclear transactions"
              >
                <Text variant={TypographyStyles.LABEL_MD}>
                  Add context to unclear transactions
                </Text>
                <Text
                  variant={TypographyStyles.BODY_SM}
                  className="text-neutral-600"
                >
                  <List bulleted className="mt-2 ml-2">
                    <List.Item className="mb-1">
                      Open the <strong>Needs Review</strong> tab to see flagged
                      transactions.
                    </List.Item>
                    <List.Item className="mb-1">
                      Click the <strong>•••</strong> menu and leave a note
                      describing what the transaction was for.
                    </List.Item>
                    <List.Item className="mb-0">
                      A bookkeeper will categorize it from there.
                    </List.Item>
                  </List>
                </Text>
              </ListCustomBullets.Item>
            </ListCustomBullets>
            <Link
              href="https://www.loom.com/share/3ba4041bcef74cc4b361b9fa82f28c15"
              newPage
              className="@min-[800px]:hidden"
              iconLeft={faCirclePlay}
              iconRight={faArrowRight}
            >
              View video walkthrough
            </Link>
          </div>
        </div>
      </div>
    </Card>
  )
}

export default TransactionsOnboardingCard
